import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import { getAsset } from '../../../../../../../../../util/assets';
import Button from '../../../../../../../../../components/global/Button';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Badge from '../../../../../../../../../components/global/Badge';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../RequiresPrintPackageAlert';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import { getPrintProductSizesForPackagesAndPricing } from '../../../../../../AlbumFolder/AlbumFolders/AlbumFolderPictureDay/PrintPackageForm/helpers';

const propTypes = {
  className: PropTypes.string,
  printProduct: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    preview_asset_image: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    sizes: PropTypes.shape({
      total: PropTypes.number.isRequired,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  }).isRequired,
  readOnly: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onPrintProductSelect: PropTypes.func.isRequired,
  onPrintProductSizeSelected: PropTypes.func.isRequired,
  getPrintProductSizePrice: PropTypes.func,
  disabled: PropTypes.bool,
  disabledElement: PropTypes.node,
  hasIndividualSizeCartItem: PropTypes.bool,
};

const PrintProductGroup = ({
  className,
  printProduct,
  readOnly,
  onPrintProductSelect,
  onPrintProductSizeSelected,
  getPrintProductSizePrice,
  disabled,
  disabledElement,
  hasIndividualSizeCartItem,
}) => {
  const openModal = useOpenModal();
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-print-product': true,
  });

  const isSingleSize = printProduct.sizes?.total === 1;
  const previewImage =
    printProduct.preview_asset_image?.url ??
    printProduct?.sizes?.records?.[0]?.preview_asset_image?.url;
  const singleProductPrice = isSingleSize
    ? getPrintProductSizePrice?.(printProduct, printProduct.sizes.records[0]) ||
      printProduct.sizes.records[0].price
    : undefined;
  const availablePrintProductSizes = getPrintProductSizesForPackagesAndPricing(
    printProduct,
  );

  return (
    <Fragment>
      <div
        className={classes}
        data-testid={`PrintProductGroup-${printProduct.id}`}
      >
        <div className="purchasing-option-item-thumb">
          <img
            width={previewImage ? 80 : 60}
            src={previewImage || getAsset('/simple-prints-illustration.svg')}
            alt=""
          />
        </div>

        <div className="purchasing-option-item-content-wrap">
          <span className="purchasing-option-item-title">
            {printProduct.name}
          </span>
          <div className="purchasing-option-item-info-extra">
            {isSingleSize && singleProductPrice ? (
              <Badge badgeStyle="yellow" className="text-extra-bold">
                <PriceDisplay
                  price={singleProductPrice.value}
                  currency={singleProductPrice.currency}
                />
              </Badge>
            ) : (
              <span className="text-primary text-sm text-extra-bold">
                {availablePrintProductSizes.length}{' '}
                {pluralize('product', availablePrintProductSizes.length)}
              </span>
            )}
          </div>

          {disabled && disabledElement && !readOnly && <>{disabledElement}</>}

          {!readOnly && (
            <Button
              className={classNames({
                'purchasing-options-item-btn': true,
                'btn-purchase-options-in-cart': hasIndividualSizeCartItem,
                'mt-2': true,
              })}
              btnStyle={classNames({
                primary: !hasIndividualSizeCartItem,
                info: hasIndividualSizeCartItem,
              })}
              data-testid="btn-print-product-shop"
              onClick={() => {
                if (disabled) {
                  openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, {
                    name: printProduct.name,
                  });
                  return;
                }
                if (isSingleSize) {
                  onPrintProductSizeSelected(printProduct.sizes?.records[0]);
                } else {
                  onPrintProductSelect(printProduct);
                }
              }}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {isSingleSize ? (
                hasIndividualSizeCartItem ? (
                  <AddedToCartLabel />
                ) : (
                  'Configure and buy'
                )
              ) : (
                'Shop'
              )}
            </Button>
          )}
          {readOnly && !isSingleSize && (
            <div data-testid={`PrintProductGroupPriceList-${printProduct.id}`}>
              {availablePrintProductSizes.map(printProductSize => {
                const productSizePrice =
                  getPrintProductSizePrice?.(printProduct, printProductSize) ??
                  printProductSize.price;

                if (!productSizePrice) {
                  return null;
                }

                return (
                  <div
                    key={printProductSize.id}
                    className="d-flex flex-column mt-1"
                  >
                    <span className="text-sm purchasing-option-item-title">
                      {printProductSize.print_size?.name}
                    </span>
                    <div className="purchasing-option-item-info-extra">
                      <Badge badgeStyle="yellow" className="text-extra-bold">
                        <PriceDisplay
                          price={productSizePrice.value}
                          currency={productSizePrice.currency}
                        />
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

PrintProductGroup.propTypes = propTypes;

export default PrintProductGroup;
